import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { banners, comics, features, categories, testimonials } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageHead from '@/components/feature/PageHead';

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const hotComics = comics.filter((c) => c.rating >= 9.5).slice(0, 8);
  const latestComics = comics.slice(0, 8);

  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '甜柚漫画',
    url: 'https://www.tianyoucomics.com',
    description: '甜柚漫画支持下拉式阅读，无限滚动，手机下滑畅读，免费全集漫画，热门漫画一网打尽。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.tianyoucomics.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '甜柚漫画',
    url: 'https://www.tianyoucomics.com',
    description: '中国领先的免费漫画在线阅读平台，支持下拉式阅读与无限滚动体验',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(homeJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <PageHead
        title="甜柚漫画 | 甜柚漫画下拉式阅读 无限滚动 手机下滑畅读 免费全集漫画"
        description="甜柚漫画支持下拉式阅读，无限滚动，手机下滑畅读，竖屏下拉，免费全集漫画，热门漫画一网打尽。甜柚漫画,下拉式阅读,无限滚动,手机下滑,竖屏下拉,免费全集,漫画大全,热门漫画,甜柚漫画网"
        keywords="甜柚漫画,下拉式阅读,无限滚动,手机下滑,竖屏下拉,免费全集,漫画大全,热门漫画,甜柚漫画网,下拉阅读,手机流畅看漫画"
        canonicalPath="/"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Banner */}
        <section className="relative h-[420px] md:h-[600px] w-full overflow-hidden">
          {banners.map((banner, index) => (
            <div
              key={banner.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentBanner ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={banner.image}
                alt={banner.title}
                className="w-full h-full object-cover object-center"
                width="1400"
                height="600"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <div className="max-w-3xl w-full">
              <h1 className="text-3xl md:text-5xl font-heading text-white mb-3 drop-shadow-lg">
                <Link to="/" className="text-white hover:text-accent-400 transition-colors">
                  甜柚漫画
                </Link>
              </h1>
              <p className="text-sm md:text-lg text-white/90 mb-2 drop-shadow-md max-w-xl mx-auto">
                下拉式阅读 · 无限滚动 · 手机下滑畅读
              </p>
              <p className="text-xs md:text-sm text-white/70 mb-6 drop-shadow-md max-w-lg mx-auto">
                免费全集漫画，热门漫画一网打尽，竖屏下拉，沉浸式阅读体验
              </p>

              {banners.map((banner, index) => (
                <div
                  key={banner.id}
                  className={`transition-all duration-500 ${
                    index === currentBanner ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 absolute inset-0 flex flex-col items-center justify-center'
                  } ${index !== currentBanner ? 'pointer-events-none' : ''}`}
                >
                  <Link
                    to={banner.link}
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    立即阅读
                  </Link>
                </div>
              ))}
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentBanner
                    ? 'bg-primary-500 w-6'
                    : 'bg-white/60 hover:bg-white/80'
                }`}
                aria-label={`切换到第${index + 1}个Banner`}
              />
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="text-center p-5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors duration-300"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${feature.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{feature.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Comics */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  热门漫画
                </h2>
                <p className="text-xs md:text-sm text-foreground-500 mt-1">全网最火的漫画都在这里</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Category Quick Access */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                漫画分类
              </h2>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">多元题材，总有一款适合你</p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap bg-background-100 text-foreground-700 hover:bg-primary-500 hover:text-background-50 cursor-pointer"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  <i className="ri-time-line text-accent-500 mr-2"></i>
                  最近更新
                </h2>
                <p className="text-xs md:text-sm text-foreground-500 mt-1">第一时间追更，不错过任何精彩</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {latestComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Ratings */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                漫友口碑
              </h2>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">来看看漫友们怎么说</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 rounded-lg p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.comment}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" width="40" height="40" />
                    </div>
                    <span className="text-sm font-semibold text-foreground-900">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 md:py-14 bg-primary-500">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-heading text-background-50">12,000+</div>
                <p className="text-sm text-background-50/80 mt-2">精选漫画</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading text-background-50">500万+</div>
                <p className="text-sm text-background-50/80 mt-2">日活跃读者</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading text-background-50">8,000万+</div>
                <p className="text-sm text-background-50/80 mt-2">总阅读次数</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading text-background-50">99.9%</div>
                <p className="text-sm text-background-50/80 mt-2">用户好评率</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20atmospheric%20Chinese%20style%20background%20with%20soft%20gradients%20of%20coral%20and%20cream%20tones%2C%20abstract%20flowing%20shapes%20reminiscent%20of%20comic%20speed%20lines%2C%20gentle%20warm%20lighting%2C%20minimalist%20elegant%20design%2C%20dreamy%20soft%20atmosphere&width=1400&height=500&seq=cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
            width="1400"
            height="500"
          />
          <div className="absolute inset-0 bg-background-50/70"></div>
          <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
              现在就开始你的漫画之旅
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-6">
              海量热门漫画免费看，竖屏下拉式阅读，随时随地畅享漫画世界
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line"></i>
              开始阅读
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}